/**
 * What Pendo is told about a session, from whichever source had it.
 *
 * An absent property is one not yet known, and is left out of what Pendo is
 * sent — Pendo keeps a property's last value, so omitting it can't skew
 * anything. `null` is a known "none" and is sent.
 */
export interface PendoProfile {
    visitor: PendoProfile.Visitor;
    account: PendoProfile.Account;
}

export namespace PendoProfile {
    export interface Visitor {
        id: string;
        email?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        /** Epoch milliseconds. */
        createdAt?: number | null;
        /** Epoch milliseconds. */
        lastLoginAt?: number | null;
        internal?: boolean;
        authorities?: string[];
    }

    export interface Account {
        id: string;
        name?: string | null;
        subdomain?: string | null;
        /** Epoch milliseconds. */
        createdAt?: number | null;
        planTier?: string | null;
        beta?: boolean | null;
        demo?: boolean | null;
        /** Epoch milliseconds. */
        cancelledAt?: number | null;
        modules?: Modules;
    }

    export interface Modules {
        billing: boolean;
        timeTracking: boolean;
        leads: boolean;
        multiLocation: boolean;
        clientAccess: boolean;
        clientAccessProjects: boolean;
        clientAccessRequests: boolean;
        clientAccessInvoicing: boolean;
        clientMessages: boolean;
        stripeConnected: boolean;
        qboPaymentFlowMode: string;
        adobeSign: boolean;
        rightSignature: boolean;
        documentExports: boolean;
    }

    export interface Ids {
        visitorId: string;
        accountId: string;
    }

    export function ids(profile: PendoProfile | Ids): Ids {
        const { visitor, visitorId, account, accountId } =
            profile as PendoProfile & Partial<Ids>;
        return {
            visitorId: visitorId ?? visitor?.id,
            accountId: accountId ?? account?.id,
        };
    }

    export function fromIds({ visitorId, accountId }: Ids): PendoProfile {
        return { visitor: { id: visitorId }, account: { id: accountId } };
    }

    export function hasSameIds(
        a: PendoProfile | Ids,
        b: PendoProfile | Ids,
    ): boolean {
        const aIds = ids(a);
        const bIds = ids(b);
        return (
            aIds.visitorId === bIds.visitorId &&
            aIds.accountId === bIds.accountId
        );
    }

    /**
     * `profile`, with whatever it doesn't know taken from `from`. The ids are
     * always `profile`'s.
     */
    export function fillMissing(
        profile: PendoProfile,
        from: PendoProfile,
    ): PendoProfile {
        return {
            visitor: {
                ...knownOnly(from.visitor),
                ...knownOnly(profile.visitor),
                id: profile.visitor.id,
            },
            account: {
                ...knownOnly(from.account),
                ...knownOnly(profile.account),
                id: profile.account.id,
            },
        };
    }
}

function knownOnly<T extends object>(values: T): Partial<T> {
    return Object.fromEntries(
        Object.entries(values).filter(([, value]) => value !== undefined),
    ) as Partial<T>;
}
